import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_KEY = "dashboard_pageSize";
const MAX_PAGE_SIZE = 100;

export interface UsePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  total?: number;
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToPage: (page: number) => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

/**
 * Get stored page size from localStorage, with fallback to default
 */
function getStoredPageSize(defaultSize: number): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 1 && parsed <= MAX_PAGE_SIZE) {
        return parsed;
      }
    }
  } catch {
    // localStorage may not be available
  }
  return defaultSize;
}

/**
 * Hook to manage pagination state and navigation
 * Persists pageSize to localStorage and page to URL for consistency across navigation
 * @param options - Pagination configuration options
 * @returns Pagination state and navigation functions
 */
export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const { initialPage = 1, initialPageSize = 100, total = 0 } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSizeState] = useState(() => getStoredPageSize(initialPageSize));

  // Derive page directly from URL (single source of truth)
  const page = (() => {
    const urlPage = searchParams.get("page");
    if (urlPage) {
      const parsed = parseInt(urlPage, 10);
      if (!isNaN(parsed) && parsed >= 1) {
        return parsed;
      }
    }
    return initialPage;
  })();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Persist pageSize to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(pageSize));
    } catch {
      // localStorage may not be available
    }
  }, [pageSize]);

  // Calculate 1-indexed start and end for display
  const startIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);

  const canGoNext = page < totalPages;
  const canGoPrevious = page > 1;

  // Update URL with page number (preserves other params)
  // Note: We don't validate against total here to avoid race conditions
  // The page will be validated at render time when total is known
  const setPage = useCallback(
    (newPage: number) => {
      // Validate minimum (page >= 1)
      const validPage = Math.max(1, newPage);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (validPage === 1) {
            params.delete("page"); // Clean URL for page 1
          } else {
            params.set("page", String(validPage));
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPageSize = useCallback(
    (newSize: number) => {
      // Clamp page size between 1 and MAX_PAGE_SIZE
      const clampedSize = Math.max(1, Math.min(newSize, MAX_PAGE_SIZE));
      setPageSizeState(clampedSize);
      // Reset to page 1 when page size changes
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete("page");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const nextPage = useCallback(() => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  }, [page, totalPages, setPage]);

  const previousPage = useCallback(() => {
    if (page > 1) {
      setPage(page - 1);
    }
  }, [page, setPage]);

  const goToPage = useCallback(
    (targetPage: number) => {
      setPage(targetPage);
    },
    [setPage]
  );

  return {
    page,
    pageSize,
    totalPages,
    startIndex,
    endIndex,
    setPage,
    setPageSize,
    nextPage,
    previousPage,
    goToPage,
    canGoNext,
    canGoPrevious,
  };
}
